import React, { useState, useContext } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowRight, ArrowLeft, ShieldAlert, ShieldOff,
  Trash2, MoreHorizontal, Zap, Link2, CheckCircle2, PackageX, Clock3,
} from "lucide-react";
import ReworkIcon from "../icons/ReworkIcon";
import { MfgGateToggles, ErpCodeChip, ERP_VISIBLE_STAGES, statusTriggerCls } from "./StageRowDesktop";
import StatusGlyph from "../icons/StatusGlyph";
import { FreshDot } from "../LiveIndicator";
import { BuildTypeToggle, ChildPartsPill, PartialProcuredTag } from "../bom/BomBits";
import { isAssembly } from "../bom/bomUtils";
import {
  PROCUREMENT_STATUSES, MANUFACTURING_STATUSES, QC_STATUSES, DISPATCH_STATUSES,
  parseDate, formatEtaShort, formatDateDMY, TrInvoiceCell,
} from "./utils";
import { DivisionConfigContext, DataActionsContext } from "@/App";
import { itemDivision, divArea, divFlag, divStatusOptions, divStatusLabel, divTerminalKey } from "@/lib/divisions";

const ETA_FIELD = {
  material_status: "material_eta",
  tooling_status:  "tooling_eta",
  gauging_status:  "gauging_eta",
};

export const StageRowMobile = React.memo(
  function StageRowMobile({
    item,
    stage,
    stageColor,
    isLastStage,
    isFirstStage,
    canEdit,
    canDelete,
    showValue,
    showSpend,
    clientColor,
    clientLogoSvg,
    isAnimating,
    isConfirmingDelete,
    advanceable,
    onAdvanceClick,
    onReturnClick,
    onReturnToCustomer,
    onBlockClick,
    onUnblock,
    onDeleteClick,
    onDeleteConfirmInline,
    onDeleteCancel,
    onNextActionClick,
    onDetailsClick,
    onProcStatusChange,
    onOpenBom,
    onBuildTypeChange,
    canDefineBom,
    onMfgStatusChange,
    onQcStatusChange,
    onQdToggle,
    onDispatchStatusChange,
    onSpendClick,
    onSetRouter,
    onSetKitting,
    onErpClick,
    erpHighlight,
    isAdmin,
    canErpEdit,
  }) {
    // Per-division pipeline config (Phase 1b) — mobile parity with the
    // desktop row: config statuses/labels, sub-team filter, gates, remark.
    const { config: divCfg } = useContext(DivisionConfigContext) || {};
    const { updateStatus: ctxUpdateStatus, setDesignApproved } = useContext(DataActionsContext) || {};
    const itemDiv = itemDivision(item);
    const procCfgArea   = divArea(divCfg, itemDiv, "procurement");
    const procSubteams  = procCfgArea.subteams || ["Material", "Tooling", "Gauging"];
    const showSpecialRemark = !!procCfgArea.special_remark;
    const procStatuses  = divStatusOptions(divCfg, itemDiv, "procurement");
    const mfgStatuses   = divStatusOptions(divCfg, itemDiv, "manufacturing");
    const qcStatuses    = divStatusOptions(divCfg, itemDiv, "qc");
    const dispStatuses  = divStatusOptions(divCfg, itemDiv, "dispatch");
    const mfgKitGate    = divFlag(divCfg, itemDiv, "manufacturing", "kitting_gate", true);
    const qcShowQd      = divFlag(divCfg, itemDiv, "qc", "qd", true);
    const qcCocGate     = divFlag(divCfg, itemDiv, "qc", "coc_gate", false);
    const showTrFields  = divFlag(divCfg, itemDiv, "dispatch", "tr_fields", true);
    const designGate    = divFlag(divCfg, itemDiv, "po_received", "design_approval_gate", false);
    const [remarkOpen, setRemarkOpen] = useState(false);
    const effectiveDueDate = item.dispatch_due_date || item.due_date;
    const isOverdue = effectiveDueDate && new Date(effectiveDueDate) < new Date() && stage !== "Completed";

    return (
      <div
        id={`row-${item.id}`}
        className={`p-3 border-b border-[hsl(var(--border))]/50 last:border-0 ${item.is_blocked ? "border-l-2 border-l-red-500 bg-red-500/5" : ""} ${isAnimating ? "item-row-advancing" : ""}`}
        data-testid={`mobile-card-${item.id}`}
      >
        {/* Row 1: client badge + flags + action icons */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            {item.new_to_stage && (
              <>
                <FreshDot label="New in stage" className="shrink-0 mt-0.5" />
              </>
            )}
            <span className="inline-flex items-center gap-1" style={{ color: clientColor }}>
              {clientLogoSvg ? (
                <span
                  className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
                  style={{ width: 14, height: 14 }}
                  dangerouslySetInnerHTML={{ __html: clientLogoSvg }}
                />
              ) : (
                <span
                  className="inline-flex items-center justify-center shrink-0 rounded-full text-[6px] font-bold leading-none"
                  style={{ width: 14, height: 14, color: clientColor, backgroundColor: clientColor + "20" }}
                >
                  {item.client.split(" ").slice(0, 2).map(w => w[0]).join("")}
                </span>
              )}
              <span className="text-[11px] font-semibold">{item.client}</span>
            </span>
            {item.is_rework && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-500 border border-teal-500/30"><ReworkIcon className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />Rework</span>}
            {item.is_qual && <span className="rl-glass-hairline rl-edge-orange text-[10px] font-bold px-1.5 py-0.5 rounded text-orange-600 dark:text-orange-400">QUAL</span>}
            {item.priority === "Expedite" && <span className="rl-gold-badge flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded"><Zap className="w-2.5 h-2.5" />Exp</span>}
            {item.is_blocked && <span className="flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-500 border border-red-500/30"><ShieldAlert className="w-2.5 h-2.5" />Blocked</span>}
            {item.qc_failed_return && <span className="flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30"><PackageX className="w-2.5 h-2.5" />Return</span>}
            {!["PO Received", "Procurement"].includes(stage) && <PartialProcuredTag item={item} onOpen={onOpenBom} />}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canEdit && !isFirstStage && (
              <button onClick={() => onReturnClick(item)} className="p-1.5 rounded-md bg-[hsl(var(--muted))]/60 text-[hsl(var(--muted-foreground))] active:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" aria-label="Send back to previous stage" data-testid={`mobile-return-${item.id}`}>
                <ArrowLeft className="w-3.5 h-3.5" />
              </button>
            )}
            {canEdit && stage === "Quality Control" && item.qc_status === "Failed" && !item.is_blocked && (
              <button onClick={() => onReturnToCustomer(item)} className="p-1.5 rounded-md bg-[hsl(var(--muted))]/60 text-[hsl(var(--muted-foreground))] active:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" aria-label="Return failed part to customer" data-testid={`mobile-return-customer-${item.id}`}>
                <PackageX className="w-3.5 h-3.5" />
              </button>
            )}
            {canEdit && (
              item.is_blocked ? (
                <button onClick={() => onUnblock(item.id)} className="p-1.5 rounded-md bg-emerald-500/10 text-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" aria-label="Unblock item" data-testid={`mobile-unblock-${item.id}`}><ShieldOff className="w-3.5 h-3.5" /></button>
              ) : (
                <button onClick={() => onBlockClick(item)} className="p-1.5 rounded-md bg-[hsl(var(--muted))]/60 text-[hsl(var(--muted-foreground))] active:text-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" aria-label="Report a problem" data-testid={`mobile-block-${item.id}`}><ShieldAlert className="w-3.5 h-3.5" /></button>
              )
            )}
            {canDelete && (
              <button onClick={() => onDeleteClick(item.id)} className="hidden" aria-label="Delete item" data-testid={`mobile-delete-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            )}
            {onDetailsClick && (
              <button
                onClick={() => onDetailsClick(item)}
                className="p-1.5 rounded-md bg-[hsl(var(--muted))]/60 text-[hsl(var(--muted-foreground))] active:text-orange-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                aria-label="Open item details"
                data-testid={`mobile-details-${item.id}`}
              >
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Item name */}
        <p className="font-semibold text-[13px] text-[hsl(var(--foreground))] leading-snug mb-1 break-words">{item.item_description}</p>

        {/* Assemblies — Single part / Assembly (PO Received) + child parts */}
        {stage === "PO Received" && (canEdit || isAssembly(item)) && (
          <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
            <BuildTypeToggle item={item} canEdit={canEdit} isCompact onChange={onBuildTypeChange}
              lockedReason={(item.bom || []).some(p => p.status !== "Pending") ? "Parts already ordered — it has to stay an assembly" : ""} />
            {isAssembly(item) && <ChildPartsPill item={item} onOpen={onOpenBom} isCompact canAdd={!!canDefineBom} />}
          </div>
        )}
        {stage === "Procurement" && isAssembly(item) && (
          <div className="mb-1.5"><ChildPartsPill item={item} onOpen={onOpenBom} isCompact canAdd={!!canDefineBom} /></div>
        )}

        {/* Block / return reason */}
        {item.is_blocked && (
          <div className="mb-1.5 flex flex-col gap-1">
            <p className="text-xs text-red-500 flex items-center gap-1.5 flex-wrap">
              <ShieldAlert className="w-3 h-3 shrink-0" />
              {item.block_reason_code && (
                <span className="font-mono-data font-bold px-1.5 py-0 rounded bg-red-500/15 border border-red-500/30 text-[10px]">{item.block_reason_code}</span>
              )}
              <span>{item.block_reason}</span>
            </p>
            {item.next_action ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onNextActionClick && onNextActionClick(item); }}
                className="text-left text-[11px] px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/5"
                data-testid={`mobile-next-action-${item.id}`}
              >
                <span className="text-amber-700 dark:text-amber-400 font-semibold">Next:</span>
                <span className="text-[hsl(var(--foreground))] ml-1">{item.next_action}</span>
                <span className="text-[hsl(var(--muted-foreground))] font-mono-data ml-1">· by {item.next_action_date}</span>
                {item.next_action_by && (
                  <span className="block text-[9px] text-[hsl(var(--muted-foreground))] mt-0.5">
                    Owner: <span className="font-semibold text-[hsl(var(--foreground))]">{item.next_action_by}</span>
                  </span>
                )}
              </button>
            ) : (
              onNextActionClick && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onNextActionClick(item); }}
                  className="self-start text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 underline decoration-dashed decoration-amber-500/60 underline-offset-2"
                  data-testid={`mobile-set-next-action-${item.id}`}
                >
                  Specify next action
                </button>
              )
            )}
          </div>
        )}
        {item.return_reason && !item.is_blocked && <p className="text-xs text-amber-600 italic mb-1.5 flex items-center gap-1"><ArrowLeft className="w-3 h-3 shrink-0" />{item.return_reason}</p>}
        {Array.isArray(item.drawing_links) && item.drawing_links.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap text-[10px] text-[hsl(var(--muted-foreground))] mb-1.5" data-testid={`drawing-links-mobile-${item.id}`}>
            <Link2 className="w-3 h-3 text-amber-500 shrink-0" />
            <span className="font-semibold text-amber-600 uppercase tracking-wider">Drawings</span>
            {item.drawing_links.map((url, i) => (
              <a
                key={i}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="font-mono-data px-1 py-0 rounded bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 active:bg-amber-500/20 hover:underline transition-colors"
                title={url}
              >
                #{i + 1}
              </a>
            ))}
          </div>
        )}

        {/* Meta row */}
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono-data text-[hsl(var(--muted-foreground))] mb-1.5">
          {item.part_number && <span className="px-1.5 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/40 text-[hsl(var(--foreground))] font-mono-data font-semibold">{item.part_number}</span>}
          <span className="font-semibold text-[hsl(var(--foreground))]/85">
            <span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">PO</span>
            {item.po_number}
          </span>
          {item.line_number && (
            <span className="font-semibold text-[hsl(var(--foreground))]/85">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">LN</span>
              {item.line_number}
            </span>
          )}
          {effectiveDueDate && (
            <span className={`flex items-center gap-0.5 ${isOverdue ? "text-red-500 font-bold" : ""}`}>
              {isOverdue && "⚠ "}{formatDateDMY(effectiveDueDate)}
            </span>
          )}
          {ERP_VISIBLE_STAGES.has(stage) && (
            <span className="basis-full">
              <ErpCodeChip
                item={item}
                stage={stage}
                canErpEdit={canErpEdit}
                isAdmin={isAdmin}
                onErpClick={onErpClick}
                highlight={erpHighlight}
                isCompact={false}
              />
            </span>
          )}
        </div>

        {/* Status dropdowns + advance */}
        <div className="flex items-center gap-1.5">
          <div className="flex-1 min-w-0">
            {stage === "Procurement" && canEdit && (
              <div className="flex flex-col gap-1">
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { label: "Mat", field: "material_status", val: item.material_status || "Pending", task: "Material" },
                    { label: "Tool", field: "tooling_status",  val: item.tooling_status  || "Pending", task: "Tooling" },
                    { label: "Gaug", field: "gauging_status",  val: item.gauging_status  || "Pending", task: "Gauging" },
                  ].filter(({ task }) => procSubteams.includes(task))
                   .filter(({ field }) => !(field === "material_status" && isAssembly(item)))
                   .map(({ label, field, val, task }) => (
                    <Select key={field} value={val} onValueChange={(nv) => onProcStatusChange(item, field, task, nv)}>
                      <SelectTrigger className={`${statusTriggerCls(val)} h-7 text-[10px]`} style={{ minWidth: 84 }}>
                        <span className="text-[9px] font-bold text-[hsl(var(--muted-foreground))] mr-0.5">{label}:</span>
                        <SelectValue><span className="truncate">{divStatusLabel(divCfg, itemDiv, "procurement", val)}</span></SelectValue>
                      </SelectTrigger>
                      <SelectContent>{(procStatuses.length ? procStatuses : PROCUREMENT_STATUSES.map(s => ({ key: s, label: s }))).map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  ))}
                </div>
                {/* Special remark (Phase 1b, drilling) — compact link/expand */}
                {showSpecialRemark && (
                  remarkOpen ? (
                    <input
                      type="text"
                      defaultValue={item.special_remark || ""}
                      autoFocus
                      placeholder="Special remark…"
                      maxLength={200}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setRemarkOpen(false); }}
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (item.special_remark || "")) ctxUpdateStatus?.(item.id, "special_remark", v); setRemarkOpen(false); }}
                      className="w-full h-7 px-2 text-[11px] rounded border border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/20 focus:outline-none focus:ring-1 focus:ring-orange-500/50"
                      data-testid={`mobile-special-remark-input-${item.id}`}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRemarkOpen(true)}
                      className={`self-start max-w-full truncate text-[10px] ${(item.special_remark || "").trim() ? "text-amber-700 dark:text-amber-400" : "text-[hsl(var(--muted-foreground))]/70"}`}
                      data-testid={`mobile-special-remark-${item.id}`}
                    >
                      {(item.special_remark || "").trim() ? `✱ ${item.special_remark}` : "+ Special remark"}
                    </button>
                  )
                )}
                {/* ETA chips for any "Ordered" subteams */}
                {(["material_status","tooling_status","gauging_status"]
                  .some(f => item[f] === "Ordered")) && (
                  <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`mobile-eta-chips-${item.id}`}>
                    {[
                      { task: "Mat",  field: "material_status", team: "Material" },
                      { task: "Tool", field: "tooling_status",  team: "Tooling" },
                      { task: "Gaug", field: "gauging_status",  team: "Gauging" },
                    ].filter(({ field, team }) => item[field] === "Ordered" && procSubteams.includes(team)).map(({ task, field }) => (
                      <span key={field} className="text-[9px] font-mono-data font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 border border-blue-500/30">
                        {task} ETA · {formatEtaShort(item[ETA_FIELD[field]])}
                      </span>
                    ))}
                  </div>
                )}
                {/* Spend history shortcut */}
                {showSpend && (() => {
                  const tot = (item.material_spend || 0) + (item.tooling_spend || 0) + (item.gauging_spend || 0);
                  if (tot > 0 && onSpendClick) {
                    return (
                      <button
                        onClick={() => onSpendClick(item)}
                        className="self-start text-[10px] font-mono-data font-bold text-emerald-600 hover:text-emerald-500 underline decoration-dotted underline-offset-2"
                        data-testid={`mobile-spend-history-${item.id}`}
                      >
                        Spent ${tot.toLocaleString("en-US", { maximumFractionDigits: 0 })} · view
                      </button>
                    );
                  }
                  if (onSpendClick) {
                    return (
                      <button
                        onClick={() => onSpendClick(item)}
                        className="self-start text-[10px] font-bold uppercase tracking-widest text-emerald-600 hover:text-emerald-500"
                        data-testid={`mobile-spend-history-${item.id}`}
                      >
                        + Add spend
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
            {stage === "Manufacturing" && canEdit && (
              <div className="flex flex-col gap-1.5 w-full">
                <MfgGateToggles item={item} onSetRouter={onSetRouter} onSetKitting={onSetKitting} showKitting={mfgKitGate} />
                <Select value={item.manufacturing_status || (mfgStatuses[0]?.key || "Queued")} onValueChange={(v) => onMfgStatusChange(item.id, v)}>
                  <SelectTrigger className={`${statusTriggerCls(item.manufacturing_status)} h-8 w-full text-xs`}>
                    <div className="flex flex-1 items-center gap-1.5 min-w-0 overflow-hidden"><StatusGlyph status={item.manufacturing_status} size={12} className="shrink-0" /><span className="truncate"><SelectValue>{divStatusLabel(divCfg, itemDiv, "manufacturing", item.manufacturing_status || (mfgStatuses[0]?.key || "Queued"))}</SelectValue></span></div>
                  </SelectTrigger>
                  <SelectContent>{(mfgStatuses.length ? mfgStatuses : MANUFACTURING_STATUSES.map(s => ({ key: s, label: s }))).map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {stage === "Quality Control" && canEdit && (
              <div className="flex flex-col gap-1.5 w-full">
                <Select value={item.qc_status} onValueChange={(v) => onQcStatusChange(item.id, v)}>
                  <SelectTrigger className={`${statusTriggerCls(item.qc_status || "Pending Inspection")} h-8 w-full text-xs`}>
                    <div className="flex flex-1 items-center gap-1.5 min-w-0 overflow-hidden"><StatusGlyph status={item.qc_status} size={12} className="shrink-0" /><span className="truncate"><SelectValue /></span></div>
                  </SelectTrigger>
                  <SelectContent>{(qcStatuses.length ? qcStatuses : QC_STATUSES.map(s => ({ key: s, label: s }))).map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
                {/* COC gate (Phase 1b, drilling) */}
                {qcCocGate && item.qc_status === "Passed" && (
                  <button
                    type="button"
                    onClick={() => ctxUpdateStatus?.(item.id, "coc_status", item.coc_status === "Complete" ? "Pending" : "Complete")}
                    className={`self-start inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${
                      item.coc_status === "Complete"
                        ? "rl-pill border-emerald-500/40 bg-emerald-500/5 text-emerald-600"
                        : "border-dashed border-amber-500/60 bg-amber-500/5 text-amber-700"
                    }`}
                    aria-pressed={item.coc_status === "Complete"}
                    data-testid={`mobile-coc-toggle-${item.id}`}
                  >
                    {item.coc_status === "Complete"
                      ? <><CheckCircle2 className="w-3.5 h-3.5" /><span>COC Complete</span></>
                      : <><Clock3 className="w-3.5 h-3.5" /><span>COC Pending</span></>}
                  </button>
                )}
                {qcShowQd && item.qc_status === "Passed" && onQdToggle && (
                  <button
                    type="button"
                    onClick={() => onQdToggle(item.id, !item.quality_dossier_ready)}
                    className={`self-start inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${
                      item.quality_dossier_ready
                        ? "rl-pill border-emerald-500/40 bg-emerald-500/5 text-emerald-600"
                        : "border-dashed border-amber-500/60 bg-amber-500/5 text-amber-700 qd-pulse"
                    }`}
                    aria-pressed={!!item.quality_dossier_ready}
                    data-testid={`mobile-qd-toggle-${item.id}`}
                  >
                    {item.quality_dossier_ready
                      ? <><CheckCircle2 className="w-3.5 h-3.5" /><span>QD Ready</span></>
                      : <><span className="w-3.5 h-3.5 rounded-full border border-dashed border-amber-500/80 qd-dash-spin" aria-hidden="true" /><span>QD Pending</span></>}
                  </button>
                )}
              </div>
            )}
            {stage === "Dispatch" && canEdit && (
              <Select value={item.dispatch_status || ""} onValueChange={(v) => onDispatchStatusChange(item, v)}>
                <SelectTrigger className={`${statusTriggerCls(item.dispatch_status)} h-8 w-full text-xs`}>
                  <div className="flex flex-1 items-center gap-1.5 min-w-0 overflow-hidden"><StatusGlyph status={item.dispatch_status} size={12} className="shrink-0" /><span className="truncate"><SelectValue /></span></div>
                </SelectTrigger>
                <SelectContent>{(dispStatuses.length ? dispStatuses : DISPATCH_STATUSES.map(s => ({ key: s, label: s }))).map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {stage === "Dispatch" && (
              <TrInvoiceCell item={item} canEdit={canEdit} showTr={showTrFields} />
            )}
            {/* Customer Design Approval gate (Phase 1b, drilling) — mobile */}
            {stage === "PO Received" && designGate && canEdit && (
              <button
                type="button"
                onClick={() => setDesignApproved?.(item.id, !item.design_approved)}
                className={`self-start inline-flex items-center gap-1.5 h-7 px-2.5 mt-1 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${
                  item.design_approved
                    ? "rl-pill border-emerald-500/40 bg-emerald-500/5 text-emerald-600"
                    : "border-dashed border-amber-500/60 bg-amber-500/5 text-amber-700"
                }`}
                aria-pressed={!!item.design_approved}
                data-testid={`mobile-design-approval-${item.id}`}
              >
                {item.design_approved
                  ? <><CheckCircle2 className="w-3.5 h-3.5" /><span>Design OK</span></>
                  : <><Clock3 className="w-3.5 h-3.5" /><span>Design approval</span></>}
              </button>
            )}
          </div>
          {canEdit && stage !== "Completed" && (
            <button
              disabled={!advanceable || isAnimating}
              onClick={() => onAdvanceClick(item)}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold shrink-0 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${advanceable ? "text-white" : "opacity-40 bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] cursor-not-allowed"}`}
              style={advanceable ? { background: stageColor } : {}}
              aria-label={stage === "PO Received" ? "Accept order" : "Advance to next stage"}
              data-testid={`mobile-advance-${item.id}`}
            >
              <ArrowRight className="w-3.5 h-3.5" />
              {stage === "PO Received" ? "Accept" : "Advance"}
            </button>
          )}
        </div>

        {/* Inline delete confirm (mobile only) */}
        {isConfirmingDelete && (
          <div className="mt-3 flex items-center gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-xs text-red-500 flex-1">Delete this item?</p>
            <button onClick={() => onDeleteConfirmInline(item.id)} className="px-2.5 py-1 rounded text-xs font-bold bg-red-500 text-white">Delete</button>
            <button onClick={onDeleteCancel} className="px-2 py-1 rounded text-xs text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]">Cancel</button>
          </div>
        )}
      </div>
    );
  },
  (prev, next) =>
    prev.item === next.item &&
    prev.canEdit === next.canEdit &&
    prev.showValue === next.showValue &&
    prev.showSpend === next.showSpend &&
    prev.clientColor === next.clientColor &&
    prev.clientLogoSvg === next.clientLogoSvg &&
    prev.isAnimating === next.isAnimating &&
    prev.isConfirmingDelete === next.isConfirmingDelete &&
    prev.advanceable === next.advanceable
);
